import React, { useState, useContext } from 'react';
import useAsyncEffect from 'use-async-effect';
import { Form } from 'react-bootstrap';
import Inputs from 'components/inputs/inputs';
import Flow from 'components/common/flow/flow';
import Panel from 'components/common/panel/panel';
import SiteContext from 'components/shared/siteContext/siteContext';
import Heading from 'components/common/heading/heading';
import { SchedulerForm } from 'components/shared/scheduler/scheduler';
import DistributorFormControl from 'components/shared/distributor/distributorFormControl';
import BackofficeReportsService from 'services/backofficeReportsService';
import { Tabs, Tab } from 'components/common/tabs/tabs';
import AppContext from 'contexts/appContext';
import BackofficeReportsHelper from 'components/navigation/backofficeReports/backofficeReportsHelper';
import ReportSelection from 'components/navigation/backofficeReports/common/reportSelection';
import ReportPeriod from 'components/navigation/backofficeReports/common/reportPeriod';
import useForm from 'hooks/form';
import _ from 'lodash';

export default function BackofficeReportsTaskCreatePage(props) {
  const { setAppArea, setAppBreadcrumb } = useContext(AppContext);

  const { setFormRef, submitForm, renderFormValidationMessage } = useForm({ protect: true });

  const [isLoaded, setIsLoaded] = useState(false);
  const [hierarchyError, setHierarchyError] = useState(null);
  const [isPeriodRelative, setIsPeriodRelative] = useState(true);
  const [relativePeriodValue, setRelativePeriodSelectedValue] = useState('');
  const [relativeOffsetStartValue, setRelativeOffsetStartValue] = useState();
  const [relativeOffsetEndValue, setRelativeOffsetEndValue] = useState();
  const [absolutePeriodStartValue, setAbsolutePeriodStartSelectedValue] = useState();
  const [absolutePeriodEndValue, setAbsolutePeriodEndSelectedValue] = useState();
  const [jobOptions, setJobOptions] = useState([]);
  const [scheduledData, setScheduledData] = useState(null);
  const [distributorData, setDistributorData] = useState(null);
  const [reportScheduleTab, setReportScheduleTab] = useState('demand');
  const [reportOutputTab, setReportOutputTab] = useState('preview');
  const [taskName, setTaskName] = useState('');
  const [recipientsList, setRecipientsList] = useState([]);
  const [hierarchyId, setHierarchyId] = useState();
  const [types, setTypes] = useState([]);
  const [selectedReportTypeId, setSelectedReportTypeId] = useState('');
  const [configurationId, setConfigurationId] = useState('');

  useAsyncEffect(async () => {
    setAppArea(AppContext.Areas.BackofficeReports, 'Task Setup');
    setAppBreadcrumb([{ name: 'Home', href: '/' }, { name: 'Backoffice Reports', href: '/BackofficeReports/Run' }, { name: 'Tasks', href: '/BackofficeReports/Tasks' }, { name: 'Create Task', selected: true }]);
    setHierarchyId(await BackofficeReportsHelper.getSiteContextHierarchy());
    setTypes(await BackofficeReportsHelper.getReportTypes());
    setRecipientsList(await BackofficeReportsService.API.getRecipientsList());
    setIsLoaded(true);
  }, []);

  const handleSaveTaskClicked = submitForm(async (cancelSubmit) => {
    if (!hierarchyId) {
      setHierarchyError('A store must be selected using the control at the top of the page.')
      return cancelSubmit();
    }
    const taskId = await BackofficeReportsService.API.insertTask({
      taskName: taskName,
      hierarchyId: hierarchyId,
      reportTypeId: selectedReportTypeId,
      configurationId: configurationId,
      isRelativeDate: isPeriodRelative,
      relativeDateValue: relativePeriodValue == null || relativePeriodValue === '' ? undefined : relativePeriodValue,
      relativeStartOffset: relativeOffsetStartValue == null || relativeOffsetStartValue === '' ? undefined : relativeOffsetStartValue,
      relativeEndOffset: relativeOffsetEndValue == null || relativeOffsetEndValue === '' ? undefined : relativeOffsetEndValue,
      absoluteStartDate: absolutePeriodStartValue == null || absolutePeriodStartValue === '' ? undefined : absolutePeriodStartValue,
      absoluteEndDate: absolutePeriodEndValue == null || absolutePeriodEndValue === '' ? undefined : absolutePeriodEndValue,
      isScheduled: reportScheduleTab === 'schedule',
      isDistributed: reportOutputTab === 'distrubute',
      scheduler: reportScheduleTab === 'schedule' ? scheduledData : undefined,
      distributor: reportOutputTab === 'distrubute' ? distributorData : undefined,
      jobOptions: jobOptions,
    });
    props.history.push(`/BackofficeReports/Task/${taskId}`);
  });

  const handleCancelClicked = () => {
    props.history.goBack();
  };

  const handleSelectSite = (id) => {
    BackofficeReportsHelper.setSiteContextHierarchy(id);
    setHierarchyId(id);
    setHierarchyError(null);
  };

  const onChangeReportType = (newReportTypeId) => {
    const newReportType = _.find(types, { id: newReportTypeId });
    setSelectedReportTypeId(newReportTypeId);

    const relDateOptions = BackofficeReportsHelper.getRelativeDateOptions(newReportType);
    if (_.findIndex(relDateOptions, { key: relativePeriodValue }) < 0) {
      if (relDateOptions && relDateOptions.length > 0) {
        onChangeRelativePeriod(_.find(relDateOptions, { default: true }).key);
      }
      else {
        setRelativePeriodSelectedValue(null);
        setRelativeOffsetStartValue(null);
        setRelativeOffsetEndValue(null);
      }
    }

    const absDateOptions = BackofficeReportsHelper.getAbsoluteDateOptions(newReportType);
    if (absDateOptions == null) {
      setAbsolutePeriodStartSelectedValue(null);
      setAbsolutePeriodEndSelectedValue(null);
    }
    else if (absDateOptions.length === 1) {
      if (absolutePeriodStartValue == null) {
        setAbsolutePeriodStartSelectedValue(new Date());
        setAbsolutePeriodEndSelectedValue(new Date());
      }
      else {
        setAbsolutePeriodEndSelectedValue(absolutePeriodStartValue);
      }
    }
    else {
      if (absolutePeriodStartValue == null) {
        setAbsolutePeriodStartSelectedValue(new Date());
        setAbsolutePeriodEndSelectedValue(new Date());
      }
      else if (absolutePeriodEndValue == null) {
        setAbsolutePeriodEndSelectedValue(absolutePeriodStartValue);
      }
    }
  };

  const onChangeRelativePeriod = (newRelativePeriod) => {
    setRelativePeriodSelectedValue(newRelativePeriod);
    if (newRelativePeriod === 'daysoff') {
      setRelativeOffsetStartValue(1);
      setRelativeOffsetEndValue(null);
    }
    else if (newRelativePeriod === 'daysrangeoff') {
      setRelativeOffsetStartValue(1);
      setRelativeOffsetEndValue(2);
    }
    else if (newRelativePeriod === 'weeksoff') {
      setRelativeOffsetStartValue(1);
      setRelativeOffsetEndValue(null);
    }
    else {
      setRelativeOffsetStartValue(null);
      setRelativeOffsetEndValue(null);
    }
  };

  const onChangeAbsolutePeriodStartValue = (value) => {
    setAbsolutePeriodStartSelectedValue(value);
    setAbsolutePeriodEndSelectedValue(value);
  };

  const onChangeAbsolutePeriodEndValue = (value) => {
    setAbsolutePeriodEndSelectedValue(value);
  };

  return (
    <Flow>
      <Flow.Header>
        <Heading>
          <SiteContext.Button loaded={isLoaded} hierarchyId={hierarchyId} onSelectSite={handleSelectSite} selectableFn={BackofficeReportsHelper.siteFilterFn} />
          <div className='flex-grow-1 d-flex justify-content-end'>
            <Inputs.Button variant='default' onClick={handleCancelClicked} iconClass='icons8-undo' label='Cancel' />
            <Inputs.Button variant='success' onClick={handleSaveTaskClicked} iconClass='icons8-save' label='Save' />
          </div>
        </Heading>
      </Flow.Header>

      <Flow.Body>
        <div className='row justify-content-md-center mx-0 mt-4'>
          <div className='col-md-10 col-lg-8 col-xl-6'>
            <Form ref={setFormRef} noValidate>
              <Panel>
                <Panel.Header>Create Task</Panel.Header>
                <Panel.Body>
                  {hierarchyError && <div className='invalid-feedback d-block mb-2 mx-0'>{hierarchyError}</div>}
                  <Form.Group>
                    <Form.Label required>Description</Form.Label>
                    <Inputs.Text required placeholder='Please enter a description of the task' description='Description' value={taskName} onValueChange={setTaskName} />
                  </Form.Group>
                  <ReportSelection
                    hierarchyId={hierarchyId}
                    types={types}
                    selectedReportTypeId={selectedReportTypeId}
                    onChangeReportType={onChangeReportType}
                    configurationId={configurationId}
                    onChangeConfigurationId={setConfigurationId}
                    jobOptions={jobOptions}
                    onChangeJobOptions={setJobOptions}
                  />
                  <ReportPeriod
                    types={types}
                    selectedReportTypeId={selectedReportTypeId}
                    relativePeriodValue={relativePeriodValue}
                    onChangeRelativePeriod={onChangeRelativePeriod}
                    relativeOffsetStartValue={relativeOffsetStartValue}
                    setRelativeOffsetStartValue={setRelativeOffsetStartValue}
                    relativeOffsetEndValue={relativeOffsetEndValue}
                    setRelativeOffsetEndValue={setRelativeOffsetEndValue}
                    absolutePeriodStartValue={absolutePeriodStartValue}
                    onChangeAbsolutePeriodStartValue={onChangeAbsolutePeriodStartValue}
                    absolutePeriodEndValue={absolutePeriodEndValue}
                    onChangeAbsolutePeriodEndValue={onChangeAbsolutePeriodEndValue}
                    isPeriodRelative={isPeriodRelative}
                    setIsPeriodRelative={setIsPeriodRelative}
                  />
                  <Form.Group>
                    <Form.Label>Schedule</Form.Label>
                    <Tabs defaultSelection='demand' selection={reportScheduleTab} onSelect={setReportScheduleTab}>
                      <Tab value='demand' title='On Demand'>
                        <div>Report will only execute when ran manually.</div>
                      </Tab>
                      <Tab value='schedule' title='Scheduled'>
                        <SchedulerForm defaultValues={scheduledData} onChange={setScheduledData} />
                      </Tab>
                    </Tabs>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Output</Form.Label>
                    <Tabs defaultSelection='preview' selection={reportOutputTab} onSelect={setReportOutputTab}>
                      <Tab value='preview' title='Preview'>
                        <div>Report output will only be displayed within the application.</div>
                      </Tab>
                      <Tab value='distrubute' title='Email'>
                        <Form.Group>
                          <Form.Label>Email Recipients</Form.Label>
                          <DistributorFormControl
                            defaultValues={distributorData}
                            recipientsList={recipientsList}
                            onCreateRecipient={async (firstName, lastName, emailAddress) => await BackofficeReportsService.API.insertRecipient(firstName, lastName, emailAddress)}
                            onChange={(selections, recipients) => {
                              setDistributorData({ subscribers: selections });
                              setRecipientsList(recipients);
                            }}
                          />
                        </Form.Group>
                      </Tab>
                    </Tabs>
                  </Form.Group>
                </Panel.Body>
                <Panel.Footer>
                  {renderFormValidationMessage()}
                  <Inputs.Button variant='default' onClick={handleCancelClicked} iconClass='icons8-undo' label='Cancel' />
                  <Inputs.Button variant='success' onClick={handleSaveTaskClicked} iconClass='icons8-save' label='Save' />
                </Panel.Footer>
              </Panel>
            </Form>
          </div>
        </div>
      </Flow.Body>
    </Flow>
  );
}
